import { useState } from "react";
import { Eye } from "@phosphor-icons/react";
import { Button, currentLocale, intlLocale } from "@ghezaa/ui";
import { Trans } from "@lingui/react/macro";
import { useAuthUser, useEndViewAs } from "../session";

/**
 * Says, on every page, that an admin is viewing the application as a role (C4): which role, which markets, that nothing
 * can be changed, and when it ends by itself. Stop works from any application; switching role happens in Core
 * (`switchHref`, Core's View as a role page). Renders nothing for someone who is themselves.
 */
export function ViewAsBanner({ switchHref }: { switchHref?: string }) {
  const me = useAuthUser();
  const stop = useEndViewAs();
  const [failed, setFailed] = useState<string | null>(null);
  const preview = me.data?.viewAs;
  if (!preview) return null;

  return (
    <ViewAsNotice
      role={preview.role}
      markets={preview.markets}
      until={preview.until}
      switchHref={switchHref}
      stopping={stop.isPending}
      error={failed}
      onStop={() => {
        setFailed(null);
        stop.mutate(undefined, { onError: (error) => setFailed(error.message) });
      }}
    />
  );
}

export type ViewAsNoticeProps = {
  role: string;
  /** Empty: every market. */
  markets: string[];
  /** ISO time it ends by itself. */
  until: string;
  switchHref?: string;
  stopping?: boolean;
  error?: string | null;
  onStop: () => void;
};

/** The band itself, without a session: the shell's banner, and the specimen on `/design`. */
export function ViewAsNotice({ role, markets, until, switchHref, stopping = false, error, onStop }: ViewAsNoticeProps) {
  const where = markets.join(currentLocale() === "ar" ? "، " : ", ");
  const ends = new Intl.DateTimeFormat(intlLocale(), { hour: currentLocale() === "ar" ? "numeric" : "2-digit", minute: "2-digit" }).format(new Date(until));

  return (
    <div role="status" className="sticky top-0 z-10 flex flex-wrap items-center gap-x-4 gap-y-2 border-b border-line bg-info-soft px-4 py-2 text-sm text-info md:px-10 print:hidden">
      <Eye className="size-4 shrink-0" aria-hidden />
      <p className="min-w-0 flex-1">
        {where ? (
          <Trans>
            Viewing as <span className="font-medium">{role}</span> in {where}, look only until you stop or until {ends}.
          </Trans>
        ) : (
          <Trans>
            Viewing as <span className="font-medium">{role}</span>, look only until you stop or until {ends}.
          </Trans>
        )}
        {error && <span className="ms-2 text-danger">{error}</span>}
      </p>
      <div className="flex items-center gap-1">
        {switchHref && (
          <Button asChild variant="ghost" size="sm">
            <a href={switchHref}>
              <Trans>Switch role</Trans>
            </a>
          </Button>
        )}
        <Button size="sm" disabled={stopping} onClick={onStop}>
          <Trans>Stop viewing</Trans>
        </Button>
      </div>
    </div>
  );
}
